// import w/ object destructuring doesn't benefit from tree shaking
import curry from 'ramda/es/curry';
import unary from 'ramda/es/unary';
import bigpicture from 'bigpicture';

import {
    find,
    findAll,
    findChild,
    getParent,
    getPrevious,
    addClass,
    removeClass,
    getHTML,
    getValue,
    hasAttribute,
    getAttribute,
    setAttribute,
    removeAttribute,
    setHTML,
    setStyle,
    onAll,
    once,
    preventDefault,
    callFnWithElementsIfExist
} from 'flightdom';

import {
    ajax,
    onClick,
    onAllClick,
    showFlashMessage,
    showNetworkErr,
    updateMiniCart,
    getModalInitialTransform
} from '../helpers';

/**
 * Display full size img in the featured img tag on a thumbnail click
 * @param { HTMLElement } galleryFeaturedImg
 * @param { NodeList } galleryThumbs
 */
function switchGalleryFeaturedImg(galleryFeaturedImg, galleryThumbs) {
    if (galleryThumbs.length < 2) return;

    onAllClick(galleryThumbs, (e) => {
        galleryThumbs.forEach((thumb) => {
            if (hasAttribute(thumb, 'data-featured')) removeAttribute(thumb, 'data-featured');
        });

        setAttribute(e.target, 'data-featured', '');
        setAttribute(galleryFeaturedImg, 'src', getAttribute(e.target, 'data-medium'));
        setAttribute(galleryFeaturedImg, 'data-bp', getAttribute(e.target, 'data-bp'));
    });
}

/**
 * Init lightbox and open it on gallery img click
 * @param { HTMLElement } galleryImg
 */
function initLightboxGallery(galleryImg) {
    onClick(galleryImg, (e) => {
        bigpicture({
            el: e.target,
            gallery: findAll('.gallery img:not([data-featured])')
        });
    });
}

/**
 * Orchestrate modal & overlay transitions
 * @param { HTMLElement } body
 * @param { HTMLElement } btn
 * @param { HTMLElement } modal
 * @param { HTMLElement } overlay
 */
function morphAddToCartModal(body, btn, modal, overlay) {
    const addClassModal = curry(addClass)(modal);
    const addClassBtn = curry(addClass)(btn);

    const closeModal = (initialState) => {
        const removeClassModal = curry(removeClass)(modal);

        // fade modal once btn is operational again & over modal
        once(btn, 'transitionend', () => {
            ['visible', 'active'].forEach(unary(removeClassModal));
            removeClass(btn, 'active');
            setStyle(modal, 'transform', '');
        });

        // once modal faded, return it to totally inactive state
        once(modal, 'transitionend', () => {
            removeClassModal('fading');
            removeClass(overlay, 'active');
        });

        removeClass(body, 'noscroll');
        removeClass(overlay, 'visible');
        removeClassModal('expanded');
        setStyle(modal, 'transform', initialState);
        removeClass(btn, 'hidden');
    };

    // as soon as btn is faded, trigger modal expansion
    once(btn, 'transitionend', () => {
        setStyle(modal, 'transform', 'translate(0) scale(1)');
        addClassModal('expanded');
        addClass(overlay, 'visible');
    });

    // for correct computation, activate modal in final state (but w/o opacity)
    // then set its start size & position
    addClass(body, 'noscroll');
    addClassModal('active');
    addClass(overlay, 'active');
    const initialState = getModalInitialTransform(btn, modal);
    setStyle(modal, 'transform', initialState);

    // give the browser just enough time to position the modal
    // then set visible
    setTimeout(() => ['fading', 'visible'].forEach(unary(addClassModal)), 100);
    ['hidden', 'active'].forEach(unary(addClassBtn));

    once(findChild(modal, '.close'), 'click', () => closeModal(initialState));
    once(findChild(modal, '.close-modal'), 'click', () => closeModal(initialState));
}

/**
 * Add product & qt to cart via xhr
 * @param { String } productId
 * @param { String } qt
 * @param { String } sizeId
 * @param { Function } callback
 */
function addToCart(productId, qt, variationId, callback) {
    ajax('POST', '/wp/wp-admin/admin-ajax.php', {
        action: 'add_to_cart',
        productId,
        qt,
        variation_id: variationId
    }, showNetworkErr, callback);
}

/**
 * Get add to cart modal infos & append them to modal
 * @param { String } productId
 * @param { String } qt
 * @param { HTMLElement } modal
 * @param { Function } callback
 */
function insertCartModalInfos(productId, qt, modal, callback) {
    ajax('POST', '/wp/wp-admin/admin-ajax.php', {
        action: 'add_to_cart_modal',
        productId,
        qt
    }, showNetworkErr, (res) => {
        setHTML(modal, res);
        callback();
    });
}

/**
 * Initalise options selection
 * @param { HTMLElement } variationsData
 * @param { NodeList } varList
 * @param { HTMLElement } addToCartBtn
 */
function initOptionsSelection(variationsData, varList, addToCartBtn) {
    if (!variationsData) return;
    const variations = JSON.parse(getHTML(variationsData));

    const findTheVariation = () => {
        const selectedAttributes = Array.from(varList).reduce((acc, cur) => {
            acc[cur.id] = cur.value;
            return acc;
        }, {});

        return variations.find((variation) => {
            const matchingPropts = Object.keys(selectedAttributes).reduce((acc, key) => {
                if (variation.attributes[key] === selectedAttributes[key]) return acc + 1;
                return acc;
            }, 0);

            return matchingPropts === Object.keys(selectedAttributes).length;
        });
    };

    onAll(varList, 'change', () => {
        const numberOfSelectedOptions = Array.from(varList)
        .reduce((acc, cur) => {
            if (cur.selectedIndex > 0) return acc + 1;
            return acc;
        }, 0);

        if (numberOfSelectedOptions === varList.length) {
            const product = findTheVariation();

            if (!product) return;
            const productId = product.variation_id;

            setAttribute(addToCartBtn, 'data-variation', productId);
        }

        else setAttribute(addToCartBtn, 'data-variation', 'null');
    });
}

/**
 * Get product id & qt, add them to cart and display confirmation modal
 * @param { HTMLElement } body
 * @param { HTMLElement } miniCartIcon
 * @param { HTMLElement } miniCart
 * @param { HTMLElement } productPageBtn Add to cart btn from product page
 * @param { HTMLElement } qtField
 * @param { HTMLElement } overlay
 */
function initAddToCart(body, miniCartIcon, miniCart, productPageBtn, qtField, overlay) {
    if (!productPageBtn) return;

    onClick(productPageBtn, (e) => {
        preventDefault(e);
        const productId = getValue(e.target);
        const variationId = getAttribute(e.target, 'data-variation');
        const modal = getPrevious(getParent(e.target));

        if (variationId === 'null') {
            showFlashMessage('error', 'Vous devez choisir les options avant d’ajouter le produit au panier');
            return;
        }

        addClass(e.target, 'loading');

        addToCart(productId, qtField.value, variationId, () => insertCartModalInfos(productId, qtField.value, modal, () => {
            removeClass(e.target, 'loading');
            morphAddToCartModal(body, e.target, modal, overlay);
            updateMiniCart(miniCartIcon, miniCart);
        }));
    });
}

const galleryFeaturedImg = find('.gallery .featured');
callFnWithElementsIfExist(switchGalleryFeaturedImg, [galleryFeaturedImg, findAll('.gallery .thumb')]);
callFnWithElementsIfExist(initLightboxGallery, [galleryFeaturedImg]); // product lightbox

initOptionsSelection(
    find('#variations-data'),
    findAll('.var-list select'),
    find('.single_add_to_cart_button')
);

initAddToCart(
    find('body'),
    find('#mini-cart-icon'),
    find('#mini-cart'),
    find('.single_add_to_cart_button'),
    find('.quantity input'),
    find('#overlay')
);
